import type { StateAction } from "@entrilia/shared/types";
import type {
  OrganizationContact,
  OrganizationContactRole,
  UpdateOrganizationContactsRequest,
} from "../../../../api/types/userManagementTypes";
import { organizationContactRoles } from "./organizationContactRoles";

export interface RoleMember {
  userId: string;
  name: string;
  email: string;
}

export interface RoleExternal {
  name: string;
  email: string;
}

export interface RoleContactGroup {
  members: RoleMember[];
  externals: RoleExternal[];
}

export type OrganizationContactsState = Record<OrganizationContactRole, RoleContactGroup>;

const normalizeEmail = (email: string) => email.trim().toLowerCase();

const allRoles = organizationContactRoles.map((roleInfo) => roleInfo.role);

export const getInitialOrganizationContactsState = (): OrganizationContactsState =>
  allRoles.reduce((state, role) => {
    state[role] = { members: [], externals: [] };
    return state;
  }, {} as OrganizationContactsState);

export const organizationContactsStateFromApi = (contacts: OrganizationContact[]): OrganizationContactsState => {
  const state = getInitialOrganizationContactsState();
  contacts.forEach((contact) => {
    contact.roles.forEach((role) => {
      const group = state[role];
      if (!group) {
        return;
      }
      if (contact.userId) {
        group.members.push({ userId: contact.userId, name: contact.name, email: contact.email });
      } else {
        group.externals.push({ name: contact.name, email: contact.email });
      }
    });
  });
  return state;
};

export const getUpdateOrganizationContactsRequest = (
  state: OrganizationContactsState
): UpdateOrganizationContactsRequest => {
  const membersByUserId = new Map<string, { userId: string; roles: OrganizationContactRole[] }>();
  const externalsByEmail = new Map<string, { name: string; email: string; roles: OrganizationContactRole[] }>();

  allRoles.forEach((role) => {
    state[role].members.forEach((member) => {
      const entry = membersByUserId.get(member.userId) ?? { userId: member.userId, roles: [] };
      entry.roles.push(role);
      membersByUserId.set(member.userId, entry);
    });
    state[role].externals.forEach((external) => {
      const key = normalizeEmail(external.email);
      const entry = externalsByEmail.get(key) ?? { name: external.name, email: external.email, roles: [] };
      entry.roles.push(role);
      externalsByEmail.set(key, entry);
    });
  });

  return {
    organizationUserContacts: [...membersByUserId.values()],
    externalContacts: [...externalsByEmail.values()],
  };
};

const getGroupSignature = (group: RoleContactGroup) =>
  [
    ...group.members.map((member) => `m:${member.userId}`),
    ...group.externals.map((external) => `e:${normalizeEmail(external.email)}|${external.name.trim()}`),
  ]
    .sort()
    .join(",");

const getStateSignature = (state: OrganizationContactsState) =>
  allRoles.map((role) => `${role}:${getGroupSignature(state[role])}`).join(";");

export const isOrganizationContactsStateDirty = (
  state: OrganizationContactsState,
  persistedState: OrganizationContactsState
) => getStateSignature(state) !== getStateSignature(persistedState);

export const getRoleContactCount = (state: OrganizationContactsState, role: OrganizationContactRole) =>
  state[role].members.length + state[role].externals.length;

export const validateOrganizationContactsState = (state: OrganizationContactsState): boolean => {
  const allRolesFilled = organizationContactRoles.every((roleInfo) => getRoleContactCount(state, roleInfo.role) >= 1);
  const withinMaxCounts = organizationContactRoles.every(
    (roleInfo) => getRoleContactCount(state, roleInfo.role) <= roleInfo.maxCount
  );

  return allRolesFilled && withinMaxCounts;
};

// Actions

export const setOrganizationContactsAction =
  (contacts: OrganizationContact[]): StateAction<OrganizationContactsState> =>
  () =>
    organizationContactsStateFromApi(contacts);

export const addRoleMembersAction =
  (role: OrganizationContactRole, members: RoleMember[]): StateAction<OrganizationContactsState> =>
  (state) => {
    const existingUserIds = new Set(state[role].members.map((member) => member.userId));
    const toAdd = members.filter((member) => !existingUserIds.has(member.userId));
    if (toAdd.length === 0) {
      return state;
    }
    return { ...state, [role]: { ...state[role], members: [...state[role].members, ...toAdd] } };
  };

export const addRoleExternalsAction =
  (role: OrganizationContactRole, externals: RoleExternal[]): StateAction<OrganizationContactsState> =>
  (state) => {
    const existingEmails = new Set(state[role].externals.map((external) => normalizeEmail(external.email)));
    const toAdd = externals
      .map((external) => ({ name: external.name.trim(), email: external.email.trim() }))
      .filter((external) => !existingEmails.has(normalizeEmail(external.email)));
    if (toAdd.length === 0) {
      return state;
    }
    return { ...state, [role]: { ...state[role], externals: [...state[role].externals, ...toAdd] } };
  };

export const removeRoleMemberAction =
  (role: OrganizationContactRole, userId: string): StateAction<OrganizationContactsState> =>
  (state) => ({
    ...state,
    [role]: { ...state[role], members: state[role].members.filter((member) => member.userId !== userId) },
  });

export const removeRoleExternalAction =
  (role: OrganizationContactRole, email: string): StateAction<OrganizationContactsState> =>
  (state) => ({
    ...state,
    [role]: {
      ...state[role],
      externals: state[role].externals.filter((external) => normalizeEmail(external.email) !== normalizeEmail(email)),
    },
  });
